import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/sharp-solid-svg-icons';
import { createContext, useContext, useState } from 'react';
import { SCView } from '../view';
import { PROPERTY_NODE_TYPES } from './core';

interface SCCodeFragmentProps {
  disabled?: boolean;
  language: keyof typeof PROPERTY_NODE_TYPES;
  items: {
    label: string;
    children: {
      label: string;
      value: string;
      info?: string;
    }[];
  }[];
}

const CodeFragmentContext = createContext<{
  disabled?: boolean;
}>({});

function Item({ item, language }: { item: any; language: keyof typeof PROPERTY_NODE_TYPES }) {
  const { disabled } = useContext(CodeFragmentContext);
  return (
    <SCView py={2} px={3} className="sce-fragment-item" title={item.info || item.value}>
      <SCView
        cursorGrab={!disabled}
        draggable={!disabled}
        title={item.label}
        truncate
        onDragStart={(e) => {
          if (disabled) return; // not allowed
          // @ts-ignore
          e.dataTransfer.setData(
            'text/plain',
            `${PROPERTY_NODE_TYPES[language].accessSymbols}${item.value}`,
          );
        }}
      >
        {item.label}
      </SCView>
    </SCView>
  );
}
function Group({ item, language }: { item: any; language: keyof typeof PROPERTY_NODE_TYPES }) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <SCView
        className="sce-fragment-group"
        py={2}
        px={3}
        borderB
        borderT
        flex
        itemsCenter
        justifyBetween
        cursorPointer
        mt={'-1px'}
        selectNone
        sticky
        top={'-1px'}
        onClick={() => {
          setOpen(!open);
        }}
      >
        <SCView textLink>{item.label}</SCView>
        <SCView w={'16px'} textDesc>
          <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
        </SCView>
      </SCView>
      {open && (
        <div>
          {item.children.map((child, index) => (
            <Item key={index} item={child} language={language} />
          ))}
        </div>
      )}
    </div>
  );
}

function SCCodeFragment({ disabled, language, items }: SCCodeFragmentProps) {
  return (
    <CodeFragmentContext.Provider value={{ disabled }}>
      <SCView wFull hFull border overflowYAuto>
        {items.map((item, index) => (
          <Group key={index} item={item} language={language} />
        ))}
      </SCView>
    </CodeFragmentContext.Provider>
  );
}

export { SCCodeFragment };
export type { SCCodeFragmentProps };

// note - daniel creating a slightly altered version of the above for selecting placeholders in generic rest query data source
function VariableItem({ item, onClick }: { item: any; onClick: any }) {
  return (
    <SCView
      py={2}
      px={3}
      hover={{
        bgGray100: true,
      }}
      title={item.info || item.value}
    >
      <SCView draggable title={item.label} truncate onClick={onClick}>
        {item.label}
      </SCView>
    </SCView>
  );
}
function VariableGroup({ item, onClick }: { item: any; onClick: any }) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <SCView
        py={2}
        px={3}
        borderB
        borderT
        bgGray300
        flex
        itemsCenter
        justifyBetween
        cursorPointer
        mt={'-1px'}
        selectNone
        sticky
        top={'-1px'}
        onClick={() => {
          setOpen(!open);
        }}
      >
        <SCView textLink>{item.label}</SCView>
        <SCView w={'16px'} textDesc>
          <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
        </SCView>
      </SCView>
      {open && (
        <div>
          {item.children.map((child, index) => (
            <VariableItem key={index} item={child} onClick={() => onClick(child.label)} />
          ))}
        </div>
      )}
    </div>
  );
}

interface SCSelectVariableProps {
  onClick: (value: string) => void;
  items: {
    label: string;
    children: {
      label: string;
      value: string;
    }[];
  }[];
}

function SCSelectVariable({ items, onClick }: SCSelectVariableProps) {
  return (
    <SCView wFull hFull border overflowYAuto>
      {items.map((item, index) => (
        <VariableGroup key={index} onClick={onClick} item={item} />
      ))}
    </SCView>
  );
}

export { SCSelectVariable };
export type { SCSelectVariableProps };
